import React from 'react';

export interface LoadingSkeletonProps {
  /** Number of placeholder rows to render. Defaults to 3. */
  readonly rows?: number;
  /** Optional override for the live-region announcement. */
  readonly label?: string;
  /** Optional layout-aware placeholders supplied by the repeater. */
  readonly children?: React.ReactNode;
}

/**
 * Lightweight placeholder shown while a DataRepeater's source is
 * `pending`. We render *N* gray bars instead of a single Spinner so
 * authors get a "list is loading" affordance that mirrors the real
 * row layout — closer to the modern card/result-list pattern than a
 * single centered spinner. HEN UI does not ship a `Skeleton` today
 * (only `Spinner`) so we keep this local rather than wait on the
 * design system (open question Q3 in plan).
 *
 * Accessibility:
 * - `role="status"` + `aria-busy="true"` ensures screen readers
 *   announce the loading state without spamming on every render.
 * - `aria-live="polite"` is implicit on `role="status"`.
 * - `aria-hidden` on the visual bars prevents redundant
 *   "graphic" announcements.
 */
export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  rows = 3,
  label = 'Loading…',
  children,
}) => {
  const safeRows = Math.max(1, Math.min(rows, 12));
  return (
    <div
      role='status'
      aria-busy='true'
      aria-label={label}
      data-testid='data-repeater-loading'
      style={{ width: '100%' }}
    >
      {children ?? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            width: '100%',
          }}
        >
          {Array.from({ length: safeRows }, (_, i) => (
            <div
              key={i}
              aria-hidden='true'
              style={{
                height: 56,
                borderRadius: 8,
                background:
                  'linear-gradient(90deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.10) 50%, rgba(0,0,0,0.06) 100%)',
                backgroundSize: '200% 100%',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const placeholder = {
  borderRadius: 6,
  background:
    'linear-gradient(90deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.10) 50%, rgba(0,0,0,0.06) 100%)',
  backgroundSize: '200% 100%',
} as const;

export const LoadingCardSkeleton: React.FC = () => (
  <div
    aria-hidden='true'
    data-testid='data-repeater-card-skeleton'
    style={{
      minHeight: 390,
      width: '100%',
      border: '1px solid rgba(0,0,0,0.12)',
      borderRadius: 12,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    }}
  >
    <div style={{ ...placeholder, height: 112 }} />
    <div style={{ ...placeholder, height: 20, width: '72%' }} />
    <div style={{ ...placeholder, height: 14, width: '48%' }} />
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div style={{ ...placeholder, height: 52 }} />
      <div style={{ ...placeholder, height: 52 }} />
    </div>
    <div style={{ ...placeholder, height: 14, width: '88%' }} />
    <div style={{ ...placeholder, height: 14, width: '76%' }} />
    <div style={{ ...placeholder, height: 40, marginTop: 'auto' }} />
  </div>
);
